import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Plus, User, LogOut, Heart } from 'lucide-react';
import { usePendingRequests } from '../hooks/usePendingRequests';
import { useChatNotifications } from '../hooks/useChatNotifications';
import { useChatEligibility } from '../hooks/useChatEligibility';
import ChatPopup from './ChatPopup';
import AnimatedMenuIcon from './AnimatedMenuIcon';
import BrandMark from './BrandMark';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { showSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const { pendingCount } = usePendingRequests();
  const { unreadCount: chatUnreadCount } = useChatNotifications();
  const { hasActiveChat } = useChatEligibility();

  const handleLogout = () => {
    logout();
    showSuccess('Déconnexion', 'Vous avez été déconnecté');
    navigate('/');
  };

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  const initials = (name: string) => {
    const parts = (name || '').trim().split(' ');
    if (!parts[0]) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const isPublic = !user;
  const marketingLinks = [
    { href: '/#confiance', label: 'Protection' },
    { href: '/#lieux', label: 'Lieux' },
    { href: '/#tarifs', label: 'Tarifs' },
  ];

  const appLinkClass = (path: string) =>
    `text-sm px-2 py-1 rounded-md ${
      location.pathname.startsWith(path) ? 'text-forest-800 font-medium' : 'text-ink-muted hover:text-ink'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-paper/90 backdrop-blur">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden text-ink"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              <AnimatedMenuIcon isOpen={mobileOpen} />
            </button>
            <BrandMark />
          </div>

          {isPublic ? (
            <>
              <nav className="hidden md:flex items-center gap-8">
                {marketingLinks.map((link) => (
                  <a key={link.href} href={link.href} className="text-sm text-ink-muted hover:text-ink">
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="hidden md:flex items-center gap-3">
                <Link to="/login" className="text-sm text-ink-muted hover:text-ink">
                  Connexion
                </Link>
                <Link to="/register" className="btn btn-primary !py-2 !px-4">
                  Essayer
                </Link>
              </div>
            </>
          ) : (
            <>
              <nav className="hidden md:flex items-center gap-5">
                <Link to="/library" className={appLinkClass('/library')}>
                  Catalogue
                </Link>
                <Link to="/favorites" className={appLinkClass('/favorites')}>
                  Favoris
                </Link>
                <Link to="/add-book" className="inline-flex items-center gap-1 text-sm text-forest-700 font-medium">
                  <Plus className="h-4 w-4" /> Déposer
                </Link>
                <Link to="/requests" className={`${appLinkClass('/requests')} relative`}>
                  Demandes
                  {pendingCount > 0 && (
                    <span className="ml-1 inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-navy-700 text-[10px] text-white">
                      {pendingCount > 9 ? '9+' : pendingCount}
                    </span>
                  )}
                </Link>
                {hasActiveChat && (
                  <button
                    type="button"
                    onClick={() => {
                      setChatOpen(true);
                      setChatMinimized(false);
                    }}
                    className="relative text-sm text-ink-muted hover:text-ink"
                  >
                    Messages
                    {chatUnreadCount > 0 && (
                      <span className="ml-1 inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-forest-600 text-[10px] text-white">
                        {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                      </span>
                    )}
                  </button>
                )}
              </nav>
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="h-9 w-9 rounded-full bg-forest-700 text-white text-sm font-medium"
                  aria-label="Compte"
                >
                  {initials(user?.name || '')}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-card py-2">
                    <p className="px-4 py-2 text-xs text-ink-faint truncate">{user?.email}</p>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-ink hover:bg-paper"
                    >
                      <User className="h-4 w-4" /> Profil
                    </Link>
                    <Link
                      to="/favorites"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex md:hidden items-center gap-2 px-4 py-2 text-sm text-ink hover:bg-paper"
                    >
                      <Heart className="h-4 w-4" /> Favoris
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-800 hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" /> Déconnexion
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {isPublic && (
            <div className="md:hidden">
              <Link to="/register" className="text-sm font-medium text-forest-700">
                Essayer
              </Link>
            </div>
          )}
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-200 pt-3 space-y-1">
            {isPublic ? (
              <>
                {marketingLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 text-ink"
                  >
                    {link.label}
                  </a>
                ))}
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-2 py-2 text-ink">
                  Connexion
                </Link>
              </>
            ) : (
              <>
                <Link to="/library" onClick={() => setMobileOpen(false)} className="block px-2 py-2">
                  Catalogue
                </Link>
                <Link to="/add-book" onClick={() => setMobileOpen(false)} className="block px-2 py-2">
                  Déposer un livre
                </Link>
                <Link to="/requests" onClick={() => setMobileOpen(false)} className="block px-2 py-2">
                  Demandes {pendingCount > 0 ? `(${pendingCount})` : ''}
                </Link>
                {hasActiveChat && (
                  <button
                    type="button"
                    className="block w-full text-left px-2 py-2"
                    onClick={() => {
                      setChatOpen(true);
                      setMobileOpen(false);
                    }}
                  >
                    Messages
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {hasActiveChat && (
        <ChatPopup
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          onMinimizeChange={setChatMinimized}
          isMinimized={chatMinimized}
        />
      )}
    </header>
  );
};

export default Navbar;
